import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod/v3'
import {
  Camera,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  GitBranch,
  ListChecks,
  Plus,
  ScrollText,
  Search,
  ShieldCheck,
  Zap,
} from 'lucide-react'
import { useSetAtom } from 'jotai'
import { Button } from '@deepguide-ai/ui/button'
import { Input } from '@deepguide-ai/ui/input'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { TableEmptyState } from '@deepguide-ai/ui/table-empty-state'
import { RunbookRunsTable } from '@/components/compliance/RunbookRunsTable'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import ScreenataPageHeader from '@/components/compliance/ScreenataPageHeader'
import { PageHeaderActions } from '@/components/layout/PageHeaderActions'
import { RunbookCreateModal } from '@/components/compliance/RunbookCreateModal'
import ProcedureTriggersSettings from '@/pages/settings/procedure-triggers'
import { screenataPageContextAtom } from '@/stores/screenataPageContextStore'

const runbooksSearchSchema = z.object({
  tab: z.enum(['catalog', 'logs', 'triggers']).optional(),
})

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/runbooks'
)({
  component: RunbooksPage,
  validateSearch: runbooksSearchSchema,
  staticData: { pageHeader: { title: 'Procedures', pad: false, scroll: false } },
})

const PAGE_SIZE = 24

type RunbooksTab = 'catalog' | 'logs' | 'triggers'

const NAV_ITEMS: {
  key: RunbooksTab
  label: string
}[] = [
  { key: 'catalog', label: 'Catalog' },
  { key: 'logs', label: 'Logs' },
  { key: 'triggers', label: 'Triggers' },
]

function RunbooksPage() {
  const navigate = useNavigate()
  const { workspaceId } = Route.useParams()
  const { tab } = Route.useSearch()
  const activeTab: RunbooksTab = tab ?? 'catalog'

  const [createModalOpen, setCreateModalOpen] = useState(false)

  const setPageContext = useSetAtom(screenataPageContextAtom)
  useEffect(() => {
    setPageContext({
      page: 'runbook-list',
      summary:
        activeTab === 'logs' ? 'Runbook run logs' : 'Runbook catalog',
    })
    return () => setPageContext(null)
  }, [setPageContext, activeTab])

  return (
    <>
      {activeTab === 'catalog' && (
        <PageHeaderActions>
          <Button
            size='sm'
            className='gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90'
            onClick={() => setCreateModalOpen(true)}
          >
            <Plus className='h-3.5 w-3.5' />
            New
          </Button>
        </PageHeaderActions>
      )}

      <div className='flex h-[calc(var(--app-vh)_-_50px)] min-h-0 min-w-0 overflow-hidden'>
        <aside className='w-[220px] shrink-0 border-r border-[#EDEDEA] bg-surface-subtle px-3 py-4 dark:border-border'>
          <nav className='space-y-1.5'>
            {NAV_ITEMS.map((item) => {
              const isActive = item.key === activeTab
              return (
                <button
                  key={item.key}
                  type='button'
                  onClick={() =>
                    navigate({
                      to: '/workspaces/$workspaceId/runbooks',
                      params: { workspaceId },
                      search: { tab: item.key },
                    })
                  }
                  className={cn(
                    'flex h-8 w-full items-center justify-between rounded-md px-3 text-left text-[13px] leading-[1.2] transition-colors',
                    isActive
                      ? 'bg-muted font-medium text-text-secondary dark:bg-selected'
                      : 'bg-transparent text-text-body-muted hover:bg-muted hover:text-text-secondary dark:text-text-subtle'
                  )}
                >
                  <span className='truncate'>{item.label}</span>
                </button>
              )
            })}
          </nav>
        </aside>

        <section className='min-h-0 min-w-0 flex-1'>
          <ScrollArea className='h-full' viewClassName='[&>div]:!block'>
            {activeTab === 'triggers' ? (
              <ProcedureTriggersSettings embedded />
            ) : activeTab === 'logs' ? (
              <RunbookLogsView workspaceId={workspaceId} />
            ) : (
              <RunbookCatalogView workspaceId={workspaceId} />
            )}
          </ScrollArea>
        </section>
      </div>

      <RunbookCreateModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        workspaceId={workspaceId}
      />
    </>
  )
}

function RunbookCatalogView({ workspaceId }: { workspaceId: string }) {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  // Debounce typing so we don't fire a server query on every keystroke.
  // Reset to page 1 whenever the active query changes.
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [page, setPage] = useState(1)

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(searchQuery.trim())
      setPage(1)
    }, 250)
    return () => window.clearTimeout(timer)
  }, [searchQuery])

  const runbooksQuery = useQuery(
    trpc.runbook.listRunbooks.queryOptions({
      q: debouncedQuery || undefined,
      page,
      pageSize: PAGE_SIZE,
    })
  )

  const items = useMemo(
    () => runbooksQuery.data?.items ?? [],
    [runbooksQuery.data]
  )
  const total = runbooksQuery.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const isFirstPage = page <= 1
  const isLastPage = page >= pageCount
  const isEmptyEverywhere =
    !runbooksQuery.isLoading && total === 0 && debouncedQuery.length === 0

  return (
    <div className='mx-auto w-full max-w-5xl px-6 pt-6 pb-8'>
      <ScreenataPageHeader
        className='mb-4'
        title='Procedures'
        description='Step-by-step procedures for compliance work: screenshots, attestations, and automated checks. Run on demand, or link to a test so each run becomes audit evidence.'
      />

      <div className='relative mb-4'>
        <Search className='text-text-placeholder pointer-events-none absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2' />
        <Input
          type='search'
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder='Search procedures by name or description…'
          className='h-9 pl-9 text-[13px]'
        />
      </div>

      {runbooksQuery.isLoading ? (
        <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className='rounded-sm border border-border bg-card p-4'
            >
              <Skeleton className='h-8 w-8 rounded-full' />
              <Skeleton className='mt-3 h-4 w-3/4' />
              <Skeleton className='mt-2 h-3 w-full' />
              <Skeleton className='mt-1 h-3 w-2/3' />
            </div>
          ))}
        </div>
      ) : isEmptyEverywhere ? (
        <TableEmptyState
          icon={ClipboardCheck}
          title='No procedures yet'
          message='Click + New to start one, or ask Vera to draft one. Link a procedure to a test in your control matrix to turn each run into audit evidence.'
        />
      ) : items.length === 0 ? (
        <div className='flex flex-col items-center justify-center rounded-sm border border-border bg-card py-14 text-center'>
          <ListChecks className='mx-auto h-9 w-9 text-icon-faint' />
          <p className='mt-3 text-[14px] text-text-secondary'>
            No procedures match your search
          </p>
          <p className='mt-1 max-w-sm text-[12px] text-text-subtle'>
            Try a different name or description, or clear the search to see
            everything.
          </p>
        </div>
      ) : (
        <>
          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
            {items.map((runbook) => {
              const title = runbook.title || 'Untitled Procedure'
              const description =
                typeof runbook.description === 'string' &&
                runbook.description.trim().length > 0
                  ? runbook.description.trim()
                  : null
              const category = getCategoryMeta(runbook.runbookCategory)
              const CategoryIcon = category.icon

              return (
                <button
                  key={runbook.id}
                  type='button'
                  className='group border-border hover:border-border-divider hover:bg-surface-subtle flex h-full flex-col items-start gap-3 rounded-sm border bg-card p-4 text-left transition-colors'
                  onClick={() => {
                    navigate({
                      to: '/workspaces/$workspaceId/runbooks/$runbookId',
                      params: { workspaceId, runbookId: runbook.id },
                    })
                  }}
                >
                  <div className='flex w-full items-center justify-between gap-2'>
                    <div
                      className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-sm',
                        category.iconClassName
                      )}
                    >
                      <CategoryIcon className='h-4 w-4' />
                    </div>
                    {/* RFC-200: published (live to triggers) vs draft. */}
                    {runbook.publishedVersion != null ? (
                      <span className='inline-flex shrink-0 items-center gap-1 rounded-full border border-status-green/30 bg-status-green-bg px-2 py-0.5 text-[10px] font-medium text-status-green-strong'>
                        <span className='h-1.5 w-1.5 rounded-full bg-status-green' />
                        Published
                      </span>
                    ) : (
                      <span className='inline-flex shrink-0 items-center gap-1 rounded-full border border-status-orange/30 bg-status-orange-bg px-2 py-0.5 text-[10px] font-medium text-status-orange-strong'>
                        <span className='h-1.5 w-1.5 rounded-full bg-status-orange' />
                        Draft
                      </span>
                    )}
                  </div>
                  <div className='min-w-0 flex-1'>
                    <div className='line-clamp-2 text-[13px] font-medium text-text-secondary group-hover:text-cobalt-strong'>
                      {title}
                    </div>
                    {description && (
                      <div className='mt-1 line-clamp-3 text-[12px] leading-snug text-text-subtle'>
                        {description}
                      </div>
                    )}
                  </div>
                </button>
              )
            })}
          </div>

          {pageCount > 1 && (
            <div className='mt-6 flex items-center justify-between gap-3'>
              <span className='text-[12px] text-text-subtle'>
                Page {page} of {pageCount} · {total} total
              </span>
              <div className='flex items-center gap-2'>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  className='gap-1.5'
                  disabled={isFirstPage || runbooksQuery.isFetching}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  <ChevronLeft className='h-3.5 w-3.5' />
                  Previous
                </Button>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  className='gap-1.5'
                  disabled={isLastPage || runbooksQuery.isFetching}
                  onClick={() =>
                    setPage((current) => Math.min(pageCount, current + 1))
                  }
                >
                  Next
                  <ChevronRight className='h-3.5 w-3.5' />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function RunbookLogsView({ workspaceId }: { workspaceId: string }) {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [page, setPage] = useState(1)

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(searchQuery.trim())
      setPage(1)
    }, 250)
    return () => window.clearTimeout(timer)
  }, [searchQuery])

  const runsQuery = useQuery(
    trpc.runbook.listWorkspaceRuns.queryOptions({
      q: debouncedQuery || undefined,
      page,
      pageSize: PAGE_SIZE,
    })
  )

  const items = useMemo(() => runsQuery.data?.items ?? [], [runsQuery.data])
  const total = runsQuery.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const isFirstPage = page <= 1
  const isLastPage = page >= pageCount
  const isEmptyEverywhere =
    !runsQuery.isLoading && total === 0 && debouncedQuery.length === 0

  return (
    <div className='mx-auto w-full max-w-5xl px-6 pt-6 pb-8'>
      <ScreenataPageHeader
        className='mb-4'
        title='Logs'
        description='Every procedure run across this workspace, most recent first. Open a run to see its steps, evidence, and output.'
      />

      <div className='relative mb-4'>
        <Search className='text-text-placeholder pointer-events-none absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2' />
        <Input
          type='search'
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder='Search logs by name…'
          className='h-9 pl-9 text-[13px]'
        />
      </div>

      {isEmptyEverywhere ? (
        <TableEmptyState
          icon={ScrollText}
          title='No runs yet'
          message='Run a procedure from the Catalog to see its execution history here.'
        />
      ) : (
        <>
          <RunbookRunsTable
            runs={items}
            isLoading={runsQuery.isLoading}
            showRunbookColumn
            onRowClick={(executionId) =>
              navigate({
                to: '/workspaces/$workspaceId/runs/$executionId',
                params: { workspaceId, executionId },
              })
            }
            emptyState='No runs match your search. Try a different procedure name, or clear the search.'
          />

          {pageCount > 1 && (
            <div className='mt-6 flex items-center justify-between gap-3'>
              <span className='text-[12px] text-text-subtle'>
                Page {page} of {pageCount} · {total} total
              </span>
              <div className='flex items-center gap-2'>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  className='gap-1.5'
                  disabled={isFirstPage || runsQuery.isFetching}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  <ChevronLeft className='h-3.5 w-3.5' />
                  Previous
                </Button>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  className='gap-1.5'
                  disabled={isLastPage || runsQuery.isFetching}
                  onClick={() =>
                    setPage((current) => Math.min(pageCount, current + 1))
                  }
                >
                  Next
                  <ChevronRight className='h-3.5 w-3.5' />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

type RunbookCategory =
  | 'guided'
  | 'screenshot'
  | 'automation'
  | 'attestation'
  | 'mixed'

const CATEGORY_META: Record<
  RunbookCategory,
  {
    label: string
    icon: typeof ClipboardCheck
    iconClassName: string
  }
> = {
  guided: {
    label: 'Guided',
    icon: ListChecks,
    iconClassName: 'bg-selected text-cobalt-strong',
  },
  screenshot: {
    label: 'Screenshot',
    icon: Camera,
    iconClassName: 'bg-status-orange-bg text-status-orange-strong',
  },
  automation: {
    label: 'Automation',
    icon: Zap,
    iconClassName: 'bg-status-green-bg text-status-green-strong',
  },
  attestation: {
    label: 'Attestation',
    icon: ShieldCheck,
    iconClassName: 'bg-status-purple-bg text-status-purple-strong',
  },
  mixed: {
    label: 'Mixed',
    icon: GitBranch,
    iconClassName: 'bg-status-blue-bg text-status-blue-strong',
  },
}

function normalizeCategory(value: unknown): RunbookCategory {
  if (
    value === 'guided' ||
    value === 'screenshot' ||
    value === 'automation' ||
    value === 'attestation' ||
    value === 'mixed'
  ) {
    return value
  }

  return 'automation'
}

function getCategoryMeta(value: unknown) {
  return CATEGORY_META[normalizeCategory(value)]
}
